import omit from 'lodash-es/omit';
import uniq from 'lodash-es/uniq';
import moment from 'moment-timezone';
import { replace } from 'redux-first-history';
import { createAppSlice } from '.';
import {
  createEquipment as createEquipmentApi,
  deleteEquipment as deleteEquipmentApi,
  editEquipment as editEquipmentApi,
  getEquipment,
  getEquipmentModels,
  getEquipments,
  linkManyTagsToEquipments as linkManyTagsToEquipmentsApi,
  linkTagToEquipment as linkTagToEquipmentApi,
  unlinkTagFromEquipment as unlinkTagFromEquipmentApi,
} from '../api/api';
import { equipmentInstance as equipmentInstanceSchema, equipmentModel as equipmentModelSchema } from '../api/schema';
import {
  EQUIPMENT_CONSTANTS,
  EquipmentBillingMethod,
  EquipmentGpioMethod,
  EquipmentModel,
  EquipmentStatus,
} from '../constants/equipmentConstants';
import { normalize } from '../utils';
import { ItemWithPermissions } from '../utils/permissions';
import { KeysToCamelCase } from '../utils/types';
import { linkBeaconToEquipment } from './beacons';
import { ClusterId } from './clusters';
import {
  DeviceId,
  attachDeviceEquipment,
  deleteEquipmentHasDevice,
  detachDeviceEquipment,
  devicePositionUpdated,
} from './devices';
import { observationsArchived } from './observations/archived';
import { DeviceSessionId } from './sessions';
import { alerting, success } from './snacks';
import { Tag, TagId } from './tags';
import { userLoggedOut } from './users';

export type EquipmentId = number;
export type EquipmentModelId = number;

type EquipmentActiveSession = {
  device_session_id: DeviceSessionId;
  parent_device_session_id: DeviceSessionId;
  from_date: string;
  to_date: string | null;
  track: { coordinates: [number, number][] };
};

export type Equipment = ItemWithPermissions & {
  id: EquipmentId;
  label: string;
  status: EquipmentStatus;
  billing_method: EquipmentBillingMethod;
  cluster_id: ClusterId;
  current_beacon_uuid?: string;
  current_device_id?: DeviceId | null;
  equipment_model: EquipmentModel;
  equipment_model_id: EquipmentModelId;
  width: number;
  duration?: number;
  min_speed_threshold: number;
  max_speed_threshold: number;
  min_gpio_detection_range: number;
  max_gpio_detection_range: number;
  gpio_default_status: boolean;
  gpio_default_unit_method: EquipmentGpioMethod;
  focused: boolean;
  color?: string;
  tags?: Tag[];
  active_session?: EquipmentActiveSession;
};

export type NormalizedEquipment = KeysToCamelCase<Equipment>;

export type EquipmentModelEntity = {
  id: EquipmentModelId;
  type: EquipmentModel;
  equipment_type: string;
};

export type NormalizedEquipmentModel = KeysToCamelCase<EquipmentModelEntity>;

type NormalizedEquipments = {
  entities: { equipmentInstance: Record<EquipmentId, NormalizedEquipment> };
  result: EquipmentId[];
};

type State = {
  isFetching: boolean;
  equipmentModelsIds: EquipmentModelId[];
  equipmentModelById: Record<EquipmentModelId, NormalizedEquipmentModel>;
  equipmentById: Record<EquipmentId, NormalizedEquipment>;
  equipmentsIds: EquipmentId[];
};

const initialState: State = {
  isFetching: false,
  equipmentModelsIds: [],
  equipmentModelById: {},
  equipmentById: {},
  equipmentsIds: [],
};

function mergeEquipments(state: State, payload: NormalizedEquipments) {
  state.equipmentById = { ...state.equipmentById, ...payload.entities.equipmentInstance };
  state.equipmentsIds = uniq(state.equipmentsIds.concat(payload.result));
}

const isSessionCurrent = (fromDate: string, toDate: string | null | undefined, now: moment.Moment) =>
  moment(fromDate).isSameOrBefore(now) && (toDate === undefined || toDate === null || moment(toDate).isAfter(now));

const equipmentsSlice = createAppSlice({
  name: 'equipments',
  initialState,
  reducers: (create) => ({
    fetchEquipments: create.asyncThunk(
      async (_: void, { dispatch }) => {
        const response = await alerting(() => getEquipments(), { dispatch });
        return normalize(response, [equipmentInstanceSchema]) as NormalizedEquipments;
      },
      {
        pending: (state) => {
          state.isFetching = true;
        },
        fulfilled: (state, { payload }) => {
          state.equipmentById = Object.fromEntries(
            Object.entries(payload.entities.equipmentInstance || {}).map(([id, equipment]) => [
              id,
              { ...equipment, focused: false },
            ]),
          );
          state.equipmentsIds = payload.result;
        },
        settled: (state) => {
          state.isFetching = false;
        },
      },
    ),
    fetchEquipment: create.asyncThunk(
      async (id: EquipmentId, { dispatch }) => {
        const response = await alerting(() => getEquipment(id), { dispatch });
        return normalize(response, equipmentInstanceSchema) as NormalizedEquipments;
      },
      {
        fulfilled: (state, { payload }) => mergeEquipments(state, payload),
      },
    ),
    fetchEquipmentModels: create.asyncThunk(
      async (_: void, { dispatch }) => {
        const response = await alerting(() => getEquipmentModels(), { dispatch });
        const { entities } = normalize(response, [equipmentModelSchema]) as {
          entities: { equipmentModel?: Record<EquipmentModelId, NormalizedEquipmentModel> };
        };
        return entities.equipmentModel ?? {};
      },
      {
        fulfilled: (state, { payload }) => {
          state.equipmentModelById = payload;
          state.equipmentModelsIds = Object.keys(payload).map(Number);
        },
      },
    ),
    createEquipment: create.asyncThunk(async (payload: Partial<Equipment>, { dispatch }) => {
      const response = (await alerting(() => createEquipmentApi(payload), { dispatch })) as Equipment;
      success('Equipment.create_success', { dispatch });
      await dispatch(equipmentsSlice.actions.fetchEquipments());
      return response;
    }),
    editEquipment: create.asyncThunk(
      async ({ id, payload }: { id: EquipmentId; payload: Partial<Equipment> }, { dispatch }) => {
        const response = await alerting(() => editEquipmentApi(id, payload), { dispatch });
        success('Equipment.update_success', { dispatch });
        if ('archived_at' in payload && payload.archived_at) {
          observationsArchived({ equipmentIds: [id] }, { dispatch });
        }
        return normalize(response, equipmentInstanceSchema) as NormalizedEquipments;
      },
      {
        fulfilled: (state, { payload }) => mergeEquipments(state, payload),
      },
    ),
    deleteEquipment: create.asyncThunk(
      async (id: EquipmentId, { dispatch }) => {
        await alerting(() => deleteEquipmentApi(id), { dispatch });
        success('Equipment.delete_success', { dispatch });
        dispatch(replace('/equipments'));
        return id;
      },
      {
        fulfilled: (state, { payload }) => {
          state.equipmentsIds = state.equipmentsIds.filter((id) => id !== payload);
          state.equipmentById = omit(state.equipmentById, payload);
        },
      },
    ),
    linkTagToEquipment: create.asyncThunk(
      async ({ equipmentId, tag }: { equipmentId: EquipmentId; tag: Tag }, { dispatch }) => {
        await alerting(() => linkTagToEquipmentApi(equipmentId, tag.id), { dispatch });
      },
      {
        fulfilled: (state, { meta: { arg } }) => {
          const equipment = state.equipmentById[arg.equipmentId];
          if (equipment) {
            equipment.tags = [...(equipment.tags || []), arg.tag];
          }
        },
      },
    ),
    unlinkTagFromEquipment: create.asyncThunk(
      async ({ equipmentId, tagId }: { equipmentId: EquipmentId; tagId: TagId }, { dispatch }) => {
        await alerting(() => unlinkTagFromEquipmentApi(equipmentId, tagId), { dispatch });
      },
      {
        fulfilled: (state, { meta: { arg } }) => {
          const equipment = state.equipmentById[arg.equipmentId];
          if (equipment) {
            equipment.tags = (equipment.tags || []).filter((tag) => tag.id !== arg.tagId);
          }
        },
      },
    ),
    linkManyTagsToEquipments: create.asyncThunk(
      async (equipmentsAndTags: { equipmentId: EquipmentId; tag: Tag }[], { dispatch }) => {
        await alerting(
          () =>
            linkManyTagsToEquipmentsApi(
              equipmentsAndTags.map(({ equipmentId, tag }) => ({
                equipment_instance_id: equipmentId,
                tag_id: tag.id,
              })),
            ),
          { dispatch },
        );
      },
      {
        fulfilled: (state, { meta: { arg } }) => {
          for (const { equipmentId, tag } of arg) {
            const equipment = state.equipmentById[equipmentId];
            if (equipment) {
              equipment.tags = [...(equipment.tags || []), tag];
            }
          }
        },
      },
    ),
  }),
  extraReducers: (builder) => {
    builder.addCase(attachDeviceEquipment.fulfilled, (state, { payload }) => {
      const attachments = Object.values(payload.entities.equipmentHasDevice || {});
      const now = moment().add(5, 'second');
      for (const equipment of Object.values(state.equipmentById)) {
        for (const attachment of attachments) {
          if (isSessionCurrent(attachment.fromDate, attachment.toDate, now)) {
            if (attachment.equipmentInstanceId === equipment.id) {
              equipment.currentDeviceId = attachment.deviceInstanceId;
              break;
            }
            if (attachment.deviceInstanceId === equipment.currentDeviceId) {
              equipment.currentDeviceId = 0;
              equipment.status = EQUIPMENT_CONSTANTS.EQUIPMENT_STATUS.AVAILABLE;
              break;
            }
          }
          if (attachment.deviceInstanceId === equipment.currentDeviceId) {
            equipment.status = EQUIPMENT_CONSTANTS.EQUIPMENT_STATUS.AVAILABLE;
            equipment.currentDeviceId = 0;
          }
        }
      }
    });

    builder.addCase(detachDeviceEquipment.fulfilled, (state, { payload }) => {
      const attachments = Object.values(payload.entities.equipmentHasDevice || {});
      const now = moment().add(5, 'second');
      for (const equipment of Object.values(state.equipmentById)) {
        for (const attachment of attachments) {
          if (
            !isSessionCurrent(attachment.fromDate, attachment.toDate, now) &&
            attachment.deviceInstanceId === equipment.currentDeviceId
          ) {
            equipment.currentDeviceId = 0;
            equipment.status = EQUIPMENT_CONSTANTS.EQUIPMENT_STATUS.AVAILABLE;
            break;
          }
        }
      }
    });

    builder.addCase(deleteEquipmentHasDevice.fulfilled, (state, { payload }) => {
      const { entry } = payload;
      if (!entry || !isSessionCurrent(entry.fromDate, entry.toDate, moment())) {
        return;
      }
      const equipment = state.equipmentById[entry.equipmentInstanceId];
      if (equipment) {
        equipment.currentDeviceId = null;
      }
    });

    builder.addCase(linkBeaconToEquipment.fulfilled, (state, { payload }) => {
      const equipment = state.equipmentById[payload.equipmentID];
      if (equipment) {
        equipment.currentBeaconUuid = payload.beaconID;
        equipment.currentDeviceId = null;
      }
    });

    // update the active track of the equipment linked to the device with the new position
    builder.addCase(devicePositionUpdated, (state, { payload }) => {
      const equipment = Object.values(state.equipmentById).find((e) => e.currentDeviceId === payload.deviceId);
      if (!equipment?.activeSession) {
        return;
      }
      equipment.activeSession.toDate = payload.locationDate ?? null;
      equipment.activeSession.track.coordinates.push([payload.locationLongitude, payload.locationLatitude]);
    });

    builder.addCase(userLoggedOut, () => initialState);
  },
  selectors: {
    selectIsEquipmentsFetching: (state) => state.isFetching,
    selectEquipmentsIds: (state) => state.equipmentsIds,
    selectEquipmentsById: (state) => state.equipmentById,
    selectEquipmentModelsIds: (state) => state.equipmentModelsIds,
    selectEquipmentModelsById: (state) => state.equipmentModelById,
    selectEquipment: (state, id: EquipmentId) => state.equipmentById[id],
  },
});

export const {
  selectIsEquipmentsFetching,
  selectEquipmentsIds,
  selectEquipmentsById,
  selectEquipmentModelsIds,
  selectEquipmentModelsById,
  selectEquipment,
} = equipmentsSlice.selectors;

export const {
  fetchEquipments,
  fetchEquipment,
  fetchEquipmentModels,
  createEquipment,
  editEquipment,
  deleteEquipment,
  linkTagToEquipment,
  unlinkTagFromEquipment,
  linkManyTagsToEquipments,
} = equipmentsSlice.actions;

export default equipmentsSlice.reducer;
